import { useCallback, useEffect, useRef } from 'react';
import { trackProductEvent } from '@/lib/analytics';

// Calculator-side monetization-evidence instrumentation. Semantics are
// contracted in memory/FUNNEL_INSTRUMENTATION.md — if the trigger changes,
// that document must change in the same commit or history breaks silently.
const MAX_EDIT_COUNT = 99;

/**
 * Calculator completion: at most ONE event per calculator mount, fired only when
 * the configuration is tailored AND the server-side engine has returned an
 * authoritative result for it. The untouched default estimate, debounced
 * rerenders and failed requests never count.
 */
export function useCalculatorCompletion(prefilled = false) {
  const state = useRef({ edits: 0, tailored: prefilled, fired: false });

  const markEdit = useCallback(() => {
    const s = state.current;
    s.edits = Math.min(s.edits + 1, MAX_EDIT_COUNT);
    s.tailored = true;
  }, []);

  const markAuthoritativeResult = useCallback(() => {
    const s = state.current;
    if (s.fired || !s.tailored) return;
    s.fired = true;
    // edit_count 0 means the configuration arrived prefilled (saved estimate or
    // assessment hand-off) rather than being edited on this page.
    trackProductEvent('calculator_completed', { source: 'calculator', edit_count: s.edits });
  }, []);

  return { markEdit, markAuthoritativeResult };
}

/** Calls `onSustained` once when `target` stays ≥75% visible for `ms` uninterrupted. */
export function useSustainedView(target, { enabled = true, onSustained, ms = 2000 } = {}) {
  useEffect(() => {
    const el = target.current;
    if (!enabled || !el || !onSustained || typeof IntersectionObserver === 'undefined') return undefined;
    let timer = null;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !timer) timer = setTimeout(onSustained, ms);
      else if (!entry.isIntersecting && timer) { clearTimeout(timer); timer = null; }
    }, { threshold: 0.75 });
    observer.observe(el);
    return () => { if (timer) clearTimeout(timer); observer.disconnect(); };
  }, [target, enabled, onSustained, ms]);
}
